import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function SendAlert() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    stock_name: "",
    symbol: "",
    action: "BUY",
    date: new Date().toLocaleDateString("en-GB"),
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const selectedClient = clients.find((c) => c.user_id === form.client_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Pick a client first");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/send-notification", form);
      setResult(r.data);
      toast.success("Alert dispatched");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const StatusPill = ({ label, status, reason }) => {
    const icon = status === "sent" ? "✅" : status === "failed" ? "❌" : "⚠️";
    const color =
      status === "sent" ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
      status === "failed" ? "text-rose-700 bg-rose-50 border-rose-200" :
      "text-amber-700 bg-amber-50 border-amber-200";
    return (
      <div className={`px-3 py-2 rounded-md border text-sm ${color}`}>
        <span className="font-medium">{icon} {label}</span> → {status}
        {reason && <div className="text-xs mt-1 opacity-80">Reason: {reason}</div>}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="send-alert-page">
      <div>
        <h1 className="text-3xl font-display font-semibold text-zinc-950">Send Client Alert</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Personalized recommendation notification — fires to client email + Telegram in parallel.
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-zinc-600">Client</label>
              <select
                data-testid="alert-client-select"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select a client…</option>
                {clients.map((c) => (
                  <option key={c.user_id} value={c.user_id}>{c.name} · {c.email}</option>
                ))}
              </select>
              {selectedClient && (
                <p className="text-xs text-zinc-500 mt-2">
                  Email: <span className="font-mono">{selectedClient.email || "—"}</span>
                  {" · "}
                  Telegram: <span className="font-mono">{selectedClient.telegram_chat_id || "—"}</span>
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Action</label>
              <select data-testid="alert-action" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm">
                <option>BUY</option><option>SELL</option><option>HOLD</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Date (DD/MM/YYYY)</label>
              <Input data-testid="alert-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Stock Name</label>
              <Input data-testid="alert-stock-name" required value={form.stock_name} onChange={(e) => setForm({ ...form, stock_name: e.target.value })} placeholder="Infosys" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Symbol</label>
              <Input data-testid="alert-symbol" required value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} placeholder="INFY" className="mt-1 font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-zinc-600">Notes (optional)</label>
              <Input data-testid="alert-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional context…" className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Button data-testid="alert-submit" type="submit" disabled={sending || !form.client_id} className="bg-zinc-950 hover:bg-zinc-800 text-white">
                {sending ? "Dispatching…" : "Submit & Notify Client"}
              </Button>
            </div>
          </form>

          {result && (
            <div className="mt-6 border border-zinc-200 rounded-lg p-4 bg-zinc-50">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">Notification Status</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <StatusPill label="Email" status={result.email.status} reason={result.email.reason} />
                <StatusPill label="Telegram" status={result.telegram.status} reason={result.telegram.reason} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
